import { atom } from 'recoil';
import { service_tourService } from '@app/shared/services/tours.service';

// Atom for the currently selected user place (for viewing/editing)
export const atom_selectedUserPlace = atom<PlaceDetails | null>({
	key: 'atom_selectedUserPlace',
	default: null,
});

// PlaceDetails structure from the places service
export interface PlaceDetails {
	place_id: string;
	name: string;
	formatted_address?: string;
	geometry: {
		location: {
			lat: number;
			lng: number;
		};
	};
	types?: string[];
	icon?: string;
	business_status?: string;
	vicinity?: string;
	// Fields for dropped pins
	description?: string;
	tags?: string[];
	// Explicit place type to avoid relying on place_id prefix
	placeType?: 'dropped_pin' | 'search_place';
}

// Main atom storing user-added places per tour
// Structure: { tourId: PlaceDetails[] }
export const atom_userAddedPlaces = atom<Record<string, PlaceDetails[]>>({
	key: 'UserAddedPlaces',
	default: JSON.parse(localStorage.getItem('userAddedPlaces') || '{}'),
	effects: [
		({ onSet }) => {
			// Save to localStorage on any change (including merges from Firebase)
			onSet((newPlaces) => {
				localStorage.setItem('userAddedPlaces', JSON.stringify(newPlaces));
			});
		},
	],
});

// Helper function to merge backend stops with localStorage stops
// Backend stops take priority, localStorage stops that don't exist in backend are added
export const mergeUserStops = (
	backendStops: PlaceDetails[],
	localStorageStops: PlaceDetails[]
): PlaceDetails[] => {
	const backendIds = new Set(backendStops.map(stop => stop.place_id));

	// Find stops in localStorage that are NOT in backend
	const missingStops = localStorageStops.filter(
		localStop => !backendIds.has(localStop.place_id)
	);

	// Return backend stops + missing localStorage stops
	return [...backendStops, ...missingStops];
};

// Fetch user stops from backend and merge with localStorage
export const fetchAndMergeUserStops = async (
	tourID: string,
	setUserAddedPlaces: (updater: (prev: Record<string, PlaceDetails[]>) => Record<string, PlaceDetails[]>) => void
): Promise<PlaceDetails[]> => {
	// Get uID from credential_items in localStorage
	const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');

	if (!uID) {
		console.warn('[fetchAndMergeUserStops] No uID found in credential_items');
		return [];
	}

	// 1. Fetch stops from backend
	const backendStops = await service_tourService.getUserAddedStops(uID, tourID);

	// 2. Get current localStorage stops for this tour
	const localStorageData = JSON.parse(localStorage.getItem('userAddedPlaces') || '{}');
	const localStorageStops: PlaceDetails[] = localStorageData[tourID] || [];

	// 3. Merge: backend stops + any localStorage stops not in backend
	const mergedStops = mergeUserStops(backendStops, localStorageStops);

	// 4. Update the atom with merged stops
	setUserAddedPlaces((prev) => ({
		...prev,
		[tourID]: mergedStops,
	}));

	return mergedStops;
};
